import { Component, Input } from '@angular/core';
import { ChatbotService } from '@sabstravtech/obtservices/angular';
import { faThumbsUp, faThumbsDown } from '@fortawesome/pro-regular-svg-icons';
import { feedbackReasonsForRating } from '../chat-feedback-reasons';

/**
 * ENT-16247 — per-result-card feedback. Mirrors the message-bubble feedback
 * form (thumbs → reason chips → comment → Submit/Skip → confirmation) but is
 * self-contained so it can be dropped into each rail/flight/hotel card.
 *
 * Feedback is submitted through the same ChatbotService.submitFeedback() API
 * under a synthetic, per-card id supplied by the host card
 * (e.g. `<messageId>::rail-out-0`). Click/keydown events are stopped by the
 * host card so interacting with the form never toggles the card open.
 */
@Component({
  selector: 'app-chat-card-feedback',
  standalone: false,
  templateUrl: './chat-card-feedback.component.html',
  styleUrls: ['./chat-card-feedback.component.scss']
})
export class ChatCardFeedbackComponent {
  /** Stable synthetic id for this card's feedback record. */
  @Input() feedbackId = '';
  /** Short human-readable card summary, sent as the assistantResponse. */
  @Input() summary = '';

  faThumbsUp = faThumbsUp;
  faThumbsDown = faThumbsDown;

  rating: 'up' | 'down' | null = null;
  reason = '';
  comment = '';
  detailOpen = false;
  confirmed = false;
  activeReasons: readonly string[] = [];

  constructor(private readonly chatbot: ChatbotService) {}

  rate(rating: 'up' | 'down'): void {
    if (this.rating === rating) {
      // Second click on the same thumb toggles the detail panel.
      this.detailOpen = !this.detailOpen;
      this.activeReasons = this.detailOpen ? this.reasonsFor(rating) : [];
      return;
    }
    // Switching rating clears any previously selected reason chip.
    if (this.rating && this.rating !== rating) {
      this.reason = '';
    }
    this.rating = rating;
    this.detailOpen = true;
    this.confirmed = false;
    this.activeReasons = this.reasonsFor(rating);
  }

  selectReason(reason: string): void {
    this.reason = this.reason === reason ? '' : reason;
  }

  submit(): void {
    this.send();
    this.detailOpen = false;
    this.activeReasons = [];
    this.confirmed = true;
  }

  skip(): void {
    this.detailOpen = false;
    this.activeReasons = [];
    this.confirmed = true;
  }

  private reasonsFor(rating: 'up' | 'down'): readonly string[] {
    // Canonical reason set shared with mobile via obtservices.
    return feedbackReasonsForRating(rating);
  }

  private send(): boolean {
    if (!this.rating || !this.feedbackId) {
      return false;
    }
    return this.chatbot.submitFeedback(this.feedbackId, this.rating, {
      comment: this.comment.trim() || undefined,
      reason: this.reason || undefined,
      source: 'OBT',
      assistantResponse: this.summary || undefined
    });
  }
}
